import React from 'react';
import { Link } from 'react-router-dom';
import MainNavbar from '../components/MainNavbar';
import MainFooter from '../components/MainFooter';

// Component for changelog entries
const ChangelogEntry = ({ version, date, changes, highlight = false }) => {
  return (
    <div className={`mb-12 ${highlight ? 'bg-orange-50 p-6 rounded-lg border border-orange-100' : ''}`}>
      <div className="flex items-center mb-4">
        <h3 className="text-xl font-bold text-gray-900">Version {version}</h3>
        {highlight && (
          <span className="ml-3 px-3 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
            Latest
          </span>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-4">{date}</p>
      
      <div className="space-y-4">
        {Object.entries(changes).map(([category, items], catIndex) => (
          <div key={catIndex}>
            <h4 className="text-md font-semibold text-gray-800 mb-2">{category}</h4>
            <ul className="list-disc list-inside space-y-1">
              {items.map((item, itemIndex) => (
                <li key={itemIndex} className="text-gray-700">{item}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

const Changelog = () => {
  // Changelog data
  const changes = [
    {
      version: "1.5.0",
      date: "March 15, 2025",
      highlight: true,
      changes: {
        "New Features": [
          "Added password protection for shared links",
          "Introduced expiration dates for shared content",
          "Added usage statistics dashboard in account page"
        ],
        "Improvements": [
          "Enhanced HTML export with cleaner code structure",
          "Improved flow navigation with smoother transitions",
          "Added ability to delete specific versions while keeping others"
        ],
        "Bug Fixes": [
          "Fixed issue with flow navigation in mobile view",
          "Resolved authentication token refresh problems",
          "Fixed layout inconsistencies in exported HTML"
        ]
      }
    },
    {
      version: "1.4.0",
      date: "February 10, 2025",
      changes: {
        "New Features": [
          "Released sharing functionality for views and flows",
          "Added view tracking for shared links",
          "Introduced flow presentation mode"
        ],
        "Improvements": [
          "Enhanced revision interface with categorized change requests",
          "Improved AI clarification questions for better results",
          "Added more detailed confirmation for revision submissions"
        ],
        "Bug Fixes": [
          "Fixed account page loading issues",
          "Resolved payment processing race conditions",
          "Fixed project deletion confirmation modal"
        ]
      }
    },
    {
      version: "1.3.0",
      date: "January 5, 2025",
      changes: {
        "New Features": [
          "Introduced interactive flow creation",
          "Added flow viewing capabilities",
          "Released pay-as-you-go access pass system"
        ],
        "Improvements": [
          "Enhanced project management interface",
          "Improved version management for views",
          "Upgraded authentication system security"
        ],
        "Bug Fixes": [
          "Fixed HTML export issues with complex layouts",
          "Resolved style conflicts in mobile prototypes",
          "Fixed password reset flow"
        ]
      }
    },
    {
      version: "1.2.0",
      date: "December 10, 2024",
      changes: {
        "New Features": [
          "Added view revision capabilities",
          "Introduced version management",
          "Released HTML export functionality"
        ],
        "Improvements": [
          "Enhanced mobile device preview",
          "Improved project organization",
          "Added more detailed view information"
        ],
        "Bug Fixes": [
          "Fixed authentication issues when session expires",
          "Resolved styling inconsistencies",
          "Fixed view generation error handling"
        ]
      }
    },
    {
      version: "1.1.0",
      date: "November 15, 2024",
      changes: {
        "New Features": [
          "Released structured UI input interface",
          "Added support for mobile-specific prototypes",
          "Introduced style customization for projects"
        ],
        "Improvements": [
          "Enhanced AI prompt handling",
          "Improved user onboarding experience",
          "Added more comprehensive error handling"
        ],
        "Bug Fixes": [
          "Fixed registration completion issues",
          "Resolved project creation errors",
          "Fixed navigation issues after authentication"
        ]
      }
    },
    {
      version: "1.0.0",
      date: "October 31, 2024",
      changes: {
        "New Features": [
          "Initial release of Dessino.ai prototyping platform",
          "Basic view generation from text descriptions",
          "User authentication and account management",
          "Project management capabilities"
        ],
        "Core Features": [
          "AI-powered prototype generation",
          "User registration and login",
          "Project creation and management",
          "Basic view generation from text"
        ]
      }
    }
  ];

  return (
    <div className="font-sans antialiased text-gray-900">
      {/* Navbar */}
      <MainNavbar />
      
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold mb-4">Changelog</h1>
          <p className="text-xl mb-4 max-w-2xl mx-auto">
            Track our journey and see how Dessino.ai has evolved over time.
          </p>
        </div>
      </section>
      
      {/* Changelog Content */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4">
          {changes.map((entry, index) => (
            <ChangelogEntry 
              key={index}
              version={entry.version}
              date={entry.date}
              changes={entry.changes}
              highlight={entry.highlight}
            />
          ))}
        </div>
      </section>
      
      {/* Looking Forward Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">Looking to the Future</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Want to see what's coming next? Check out our product roadmap for upcoming features.
          </p>
          <Link to="/roadmap" className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors inline-block">
            View Roadmap
          </Link>
        </div>
      </section>
      
      {/* Footer */}
      <MainFooter />
    </div>
  );
};

export default Changelog;